import store from "@/store";
import {
  getModule,
  Module,
  VuexModule,
  Mutation,
  MutationAction,
} from "vuex-module-decorators";
import { doGet, doPost } from "@/store/common/module/apis";
import {
  Country,
  City,
  State,
  Lang,
  Currency,
  Zone,
} from "@/store/common/model/master";

@Module({
  namespaced: true,
  name: "common",
  store,
  dynamic: true,
})
class CommonModule extends VuexModule {
  loading = false;
  error = "";
  message = "";
  warning = "";
  translates: { [key: string]: { [key: string]: string[] } } = {};
  countries: Country[] = [];
  cities: { [key: string]: City[] } = {};
  cityGroups: { [key: string]: City[] } = {};
  states: { [key: string]: State[] } = {};
  tariffTypes: { [key: string]: string } | null = null;
  languages: Lang[] = [];
  zones: Zone[] = [];
  language: Lang = {
    name: "en",
    pic: "",
  } as Lang;
  lang: string | null = null;
  currencies: Currency[] = [];
  restore= false;

  @Mutation
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  @Mutation
  setError(error: string) {
    this.error = error;
  }

  @Mutation
  showMessage(message: string) {
    this.message = message;
  }

  @Mutation
  showWarning(warning: string) {
    this.warning = warning;
  }

  @Mutation
  setRestore(restore: boolean) {
    this.restore = restore;
  }

  @MutationAction
  async setLang(language: Lang) {
    // console.log(language);
    this.lang = language.code;
    this.language = language;
    await doPost("/api/lang/update", { lang: language.code });
    return { lang: language.code, language: this.language };
  }

  @MutationAction
  async populateTranslates() {
    try {
      let lang = this.state ? this.state["lang"] : "en";
      if (lang.length > 3) {
        lang = "en";
      }
      const translates = this.state ? this.state["translates"] : {};
      if (
        Object.keys(translates).length == 0 ||
        Object.keys(translates[lang]).length == 0
      ) {
        translates[lang] = await doGet("/api/o/i18n/" + lang + "/all");
      }
      return { translates: translates };
    } catch (error) {
      console.log(error);
    }
  }

  @MutationAction
  async fetchTranslate(key: string) {
    try {
      const translates = this.state ? this.state["translates"] : null;
      if (translates[key] == undefined) {
        const val = await doGet(
          "/api/o/i18n/translate?lang=" + this.lang + "&key=" + key
        );
        translates[key] = val;
        return { translates };
      }
      return { translates: translates };
    } catch (error) {
      console.log(error);
    }
  }

  @Mutation
  translate(key: string) {
    try {
      const translates = this.translates;
      const lang = this.lang;
      if (
        translates != null &&
        lang != null &&
        translates[lang][key] == undefined
      ) {
        this.fetchTranslate(key).then(() => {
          console.log("1");
        });
      }
      return key;
    } catch (error) {
      console.log(error);
    }
    return key;
  }

  @MutationAction
  async populateCountries() {
    try {
      let countries = this.state ? this.state["countries"] : null;
      if (countries.length == 0) {
        countries = await doGet("/api/o/country/all");
        if (countries && countries.length > 0) {
          this.countries = countries as Country[];
          return { countries: countries };
        }
      }
      return { countries: countries };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateCities(country: string) {
    const cities = this.state ? this.state["cities"] : null;
    try {
      if (country == null || country == "") return { cities: cities };
      if (cities[country] == undefined) {
        const cs = await doGet("/api/o/country/" + country + "/cities");
        if (cs && cs.length > 0) {
          cities[country] = cs as City[];
        }
        return { cities: cities };
      }
    } catch (error) {
      return Promise.reject(error);
    }
    return { cities: cities };
  }

  @MutationAction
  async rePopulateCities(country: string) {
    const cities = this.state ? this.state["cities"] : null;
    try {
      if (country == null || country == "") return { cities: cities };
        const cs = await doGet("/api/o/country/" + country + "/cities");
        if (cs && cs.length > 0) {
          cities[country] = cs as City[];
        }
        return { cities: cities };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateCityGroups(country: string) {
    try {
      const cityGroups = this.state ? this.state["cityGroups"] : null;
      if (country == null || country == "") return { cityGroups: cityGroups };
      if (cityGroups[country] == undefined) {
        const cs = await doGet("/api/citygroups/" + country + "/list");
        if (cs && cs.length > 0) {
          cityGroups[country] = cs as City[];
        }
        return { cityGroups: cityGroups };
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateStates(country: string) {
    try {
      const states = this.state ? this.state["states"] : null;
      if (country == null || country == "") return { states: states };
      if (states[country] == undefined) {
        const cs = await doGet("/api/o/country/" + country + "/states/list");
        if (cs && cs.length > 0) {
          states[country] = cs as State[];
        }
        return { states: states };
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateLanguages() {
    try {
      let lang = this.state ? this.state["lang"] : null;
      let languages = this.state ? this.state["languages"] : null;
      let language = this.state ? this.state["language"] : null;
      if (lang == null) {
        lang = await doGet("/api/language/mine");
      }
      if (languages.length == 0) {
        languages = await doGet("/api/o/language/allLang");
        if (languages && languages.length > 0) {
          this.languages = languages as Lang[];
          for (let i = 0; i < languages.length; i++) {
            if (languages[i].code == lang) {
              language = languages[i];
            }
          }
        }
        return { languages: languages, language: language, lang: lang };
      }
      for (let i = 0; i < languages.length; i++) {
        if (languages[i].code == lang) {
          this.language = languages[i];
        }
      }
      return { languages: languages, language: language, lang: lang };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateCurrencies() {
    try {
      if (this.currencies == undefined) {
        const currencies = await doGet("/api/o/currencies");
        if (currencies && currencies.length > 0) {
          this.currencies = currencies as Currency[];
        }
        return { currencies: this.currencies };
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateTariffTypes() {
    try {
      if (this.tariffTypes == undefined) {
        const tariffTypes = await doGet("/api/o/tariff/type/list");
        if (tariffTypes && tariffTypes.length > 0) {
          this.tariffTypes = tariffTypes;
        }
        return { tariffTypes: this.tariffTypes };
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateZones() {
    try {
      if (this.zones == undefined || this.zones == null) {
        const zones = await doGet("/api/zone/map/list");
        if (zones && zones.length > 0) {
          this.zones = zones;
        }
        return { zones: this.zones };
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

export default getModule(CommonModule);
